import { CascadiaColor } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { AriaProgressBarProps, useProgressBar } from "react-aria";

import { useColors } from "../../../hooks/useColors";
export interface LoaderProps extends AriaProgressBarProps {
  type?: "spinner" | "dots";
  size?: "small" | "medium" | "default";
  width?: string | number;
  height?: string | number;
  overlay?: boolean;
  color?: CascadiaColor;
  background?: CascadiaColor;
  testID?: string;
}

export function Loader(props: LoaderProps) {
  const {
    isIndeterminate = !props.value,
    label,
    value = 0,
    minValue = 0,
    maxValue = 100,
    testID = "loader",
    width,
    height,
    overlay,
    background,
  } = props;

  const { progressBarProps } = useProgressBar({
    ...props,
    "aria-label": props["aria-label"] || !label ? "Loading..." : undefined,
  }); // TODO: Translations
  const {
    type = "dots",
    color = "background-accent-green-strong",
    size = "default",
  } = props;
  const colors = useColors();
  const dimensions = size === "small" ? 20 : size === "medium" ? 24 : 44;
  const style = css(
    styles.container,
    {
      width,
      height,
    },
    overlay && styles.overlay,
    background && {
      backgroundColor: colors[background],
    }
  );
  if (type === "dots") {
    return (
      <div className={style} data-testid={testID}>
        <svg
          {...progressBarProps}
          width={dimensions}
          height={dimensions}
          viewBox="0 0 44 44"
          fill={colors[color]}
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle opacity="0.5" cx="5" cy="22" r="5">
            <animate
              attributeType="XML"
              attributeName="opacity"
              values="1;1;0.75;0.5;0.5;0.5;0.75;1"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
          <circle opacity="0.5" cx="22" cy="22" r="5">
            <animate
              attributeType="XML"
              attributeName="opacity"
              values="0.5;0.75;1;1;0.75;0.5;0.5;0.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
          <circle opacity="0.5" cx="39" cy="22" r="5">
            <animate
              attributeType="XML"
              attributeName="opacity"
              values="0.5;0.5;0.5;0.75;1;1;0.75;0.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>
    );
  }

  const center = 44 / 2;
  const strokeWidth = 4;
  const r = center - strokeWidth / 2;
  const c = 2 * r * Math.PI;
  const percentage = isIndeterminate
    ? 0.65
    : (value - minValue) / (maxValue - minValue);
  const offset = c - percentage * c;

  return (
    <div className={style} data-testid={testID}>
      <svg
        {...progressBarProps}
        width={dimensions}
        height={dimensions}
        viewBox="0 0 44 44"
        stroke={colors[color]}
        fill="none"
      >
        <circle
          role="presentation"
          r={r}
          cx={center}
          cy={center}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          transform={`rotate(-3 ${center} ${center})`}
        >
          {isIndeterminate && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 ${center} ${center}`}
              to={`360 ${center} ${center}`}
              dur="750ms"
              repeatCount="indefinite"
            />
          )}
        </circle>
      </svg>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  overlay: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 200,
  }),
};
